import { Presentation, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { CopyButton } from './CopyButton';
import type { WebinarMetadata } from '../../lib/contentParsers';

interface WebinarHeroProps {
  metadata: WebinarMetadata;
}

export function WebinarHero({ metadata }: WebinarHeroProps) {
  const copyText = [
    metadata.title,
    metadata.subtitle,
    metadata.bullets.length > 0
      ? '\nKey Points:\n' + metadata.bullets.map(b => `\u2022 ${b}`).join('\n')
      : '',
  ].filter(Boolean).join('\n\n');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-2xl overflow-hidden mb-6"
      style={{
        boxShadow:
          '0 0 40px rgba(37,99,235,0.25), 0 0 80px rgba(139,92,246,0.15), inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-indigo-600/20 to-purple-600/30" />

      {/* Bloom glow behind content */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[8%] top-[12%] w-[60%] h-[70%] rounded-full blur-[100px] bg-blue-500/30" />
        <div className="absolute right-[4%] bottom-[-10%] w-[45%] h-[70%] rounded-full blur-[110px] bg-purple-500/25" />
      </div>

      {/* Grid pattern (brighter) */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:32px_32px]" />

      {/* Border */}
      <div className="absolute inset-0 rounded-2xl border border-blue-400/50" />

      {/* Top highlight line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/70 to-transparent" />

      {/* Content */}
      <div className="relative p-6 md:p-8">
        {/* Label badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/30 mb-4">
          <Presentation className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">
            Your Webinar
          </span>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
          {metadata.title}
        </h1>

        {/* Subtitle */}
        {metadata.subtitle && (
          <p className="text-base md:text-lg text-slate-100 mb-4 leading-relaxed max-w-3xl">
            {metadata.subtitle}
          </p>
        )}

        {/* Bullets */}
        {metadata.bullets.length > 0 && (
          <div className="space-y-1.5 mb-4">
            {metadata.bullets.slice(0, 4).map((bullet, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-blue-400 mt-1 shrink-0" />
                <p className="text-sm text-slate-200">{bullet}</p>
              </div>
            ))}
          </div>
        )}

        {/* Copy button */}
        <CopyButton text={copyText} label="Copy Webinar Info" variant="small" />
      </div>
    </motion.div>
  );
}
